import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatBRL } from "../../lib/api";
import { hourBankName } from "../../lib/hourbank";
import {
  ArrowLeft, User, Mail, Phone, CreditCard, Calendar, Clock,
  PiggyBank, Receipt, Activity, CheckCircle2, MapPin, Pencil, Trash2, Wrench, FileSignature,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { EditClientModal, DeleteClientModal, AdjustHoursModal } from "../../components/ClientActions";

const STATUS_LABELS = {
  pending: "Pendente", pending_payment: "Aguardando pagamento", confirmed: "Confirmada",
  in_progress: "Em andamento", completed: "Concluída", no_show: "Não compareceu",
  cancelled: "Cancelada", cancelled_by_admin: "Cancelada pela administração",
  expired: "Pagamento expirado", paid: "Paga",
};
const STATUS_CLS = {
  confirmed: "bg-emerald-100 text-emerald-700", in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-gray-100 text-gray-600", no_show: "bg-orange-100 text-orange-700",
  cancelled: "bg-red-100 text-red-600", cancelled_by_admin: "bg-red-100 text-red-600",
  pending_payment: "bg-amber-100 text-amber-700", expired: "bg-gray-200 text-gray-500",
  paid: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700",
};
const SP = { timeZone: "America/Sao_Paulo" };
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR", SP) : "—");
const fmtT = (iso) => (iso ? new Date(iso).toLocaleTimeString("pt-BR", { ...SP, hour: "2-digit", minute: "2-digit" }) : "—");
const durH = (r) => ((new Date(r.end_time) - new Date(r.start_time)) / 3600000).toFixed(1).replace(".0", "");

const Badge = ({ s }) => (
  <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase font-bold ${STATUS_CLS[s] || "bg-gray-100 text-gray-600"}`}>
    {STATUS_LABELS[s] || s}
  </span>
);

const Card = ({ title, icon: Icon, children, testid }) => (
  <div className="bg-white border border-gray-100 rounded-xl shadow-sm" data-testid={testid}>
    <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2 text-sm font-bold text-[#14213D]">
      <Icon size={15} className="text-[#D4AF37]" /> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

export default function ClienteFicha() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // RC21 — ações administrativas (mesma implementação de Gestão → Clientes)
  const { user } = useAuth();
  const canDelete = ["super_admin", "admin"].includes(user?.role);
  const canAdjust = ["super_admin", "admin", "manager"].includes(user?.role);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [adjusting, setAdjusting] = useState(false);

  const load = () => api.get(`/clients/${id}/ficha`)
    .then(({ data }) => setData(data))
    .catch(() => setData(null))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="text-gray-400 py-20 text-center">Carregando ficha…</div>;
  if (!data) return <div className="text-gray-400 py-20 text-center">Cliente não encontrado.</div>;

  const { client: c, packages, reservations, invoices, events } = data;
  const now = new Date();
  const upcoming = reservations.filter(
    (r) => new Date(r.start_time) > now && ["confirmed", "in_progress", "pending_payment", "pending"].includes(r.status)
  );
  const activePkgs = packages.filter((p) => p.status === "active");
  const hoursLeft = activePkgs.reduce((s, p) => s + Math.max(0, (p.hours_total || 0) - (p.hours_used || 0)), 0);
  const paidCount = invoices.filter((i) => i.status === "paid").length;

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="cliente-ficha">
      <Link to="/admin/clientes" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#14213D]" data-testid="ficha-back">
        <ArrowLeft size={15} /> Clientes
      </Link>

      {/* Cabeçalho */}
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6" data-testid="ficha-header">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold mb-1">Ficha 360º</div>
            <h1 className="font-display text-2xl sm:text-3xl text-[#0A1128] font-light">{c.name} {c.last_name || ""}</h1>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-gray-600">
              <span className="inline-flex items-center gap-2"><Mail size={13} className="text-gray-400" /> {c.email}</span>
              {c.phone && <span className="inline-flex items-center gap-2"><Phone size={13} className="text-gray-400" /> {c.phone}</span>}
              {c.cpf && <span className="inline-flex items-center gap-2"><User size={13} className="text-gray-400" /> CPF {c.cpf}</span>}
              {c.created_at && <span className="inline-flex items-center gap-2"><Calendar size={13} className="text-gray-400" /> Cliente desde {fmtD(c.created_at)}</span>}
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-[10px] uppercase font-bold ${c.active === false ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"}`}>
            {c.active === false ? "Inativo" : "Ativo"}
          </span>
        </div>
        {/* RC21 — ações administrativas */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="ficha-actions">
          <button
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-gray-200 text-[#14213D] px-3 py-1.5 rounded hover:bg-gray-50"
            data-testid="ficha-edit-client"
          ><Pencil size={12} /> Editar cliente</button>
          {canDelete && (
            <button
              onClick={() => setDeleting(true)}
              className="inline-flex items-center gap-1.5 text-xs font-bold border border-red-200 text-red-600 px-3 py-1.5 rounded hover:bg-red-50"
              data-testid="ficha-delete-client"
            ><Trash2 size={12} /> Excluir cliente</button>
          )}
          {canAdjust && (
            <>
            <button
              onClick={async () => {
                if (!window.confirm(`Enviar o Contrato de Escritório Virtual para assinatura digital (Clicksign) para ${c.name}?`)) return;
                try {
                  const { data } = await api.post("/contracts/virtual-office/send", { client_id: c.id });
                  toast.success("Contrato enviado! Link de assinatura entregue por e-mail e WhatsApp.");
                  if (data.signing_url) window.open(data.signing_url, "_blank");
                } catch (err) {
                  const d = err.response?.data?.detail;
                  toast.error(typeof d === "string" ? d : "Falha ao enviar contrato");
                }
              }}
              className="inline-flex items-center gap-1.5 text-xs font-bold border border-gray-200 text-[#14213D] px-3 py-1.5 rounded hover:bg-gray-50"
              data-testid="ficha-send-contract"
            ><FileSignature size={12} /> Enviar Contrato (Clicksign)</button>
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post(`/contracts/clicksign/refresh?client_id=${c.id}`);
                  const signed = (data.results || []).filter((r) => r.signed).length;
                  if (!data.checked) toast.info("Nenhum contrato pendente para este cliente.");
                  else if (signed) { toast.success(`${signed} contrato(s) assinado(s)! PDF anexado ao cadastro.`); load(); }
                  else toast.info("Contrato ainda aguardando assinatura.");
                } catch (err) {
                  const d = err.response?.data?.detail;
                  toast.error(typeof d === "string" ? d : "Falha ao verificar assinatura");
                }
              }}
              className="inline-flex items-center gap-1.5 text-xs font-bold border border-gray-200 text-gray-600 px-3 py-1.5 rounded hover:bg-gray-50"
              data-testid="ficha-check-contract"
            ><CheckCircle2 size={12} /> Verificar Assinatura</button>
            </>
          )}
        </div>
        {/* Indicadores */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ficha-kpis">
          {[
            ["Reservas", reservations.length],
            ["Horas disponíveis", `${hoursLeft}h`],
            ["Pagamentos", `${paidCount} pago(s)`],
            ["Próxima reserva", upcoming[0] ? `${fmtD(upcoming[0].start_time)} ${fmtT(upcoming[0].start_time)}` : "—"],
          ].map(([label, value]) => (
            <div key={label} className="bg-gray-50 rounded-lg px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{label}</div>
              <div className="text-base font-bold text-[#14213D] mt-0.5 truncate">{value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Banco de Horas */}
        <Card title="Banco de Horas" icon={PiggyBank} testid="ficha-hourbank">
          {canAdjust && packages.length > 0 && (
            <button
              onClick={() => setAdjusting(true)}
              className="mb-4 inline-flex items-center gap-1.5 text-xs font-bold border border-gray-200 text-[#14213D] px-3 py-1.5 rounded hover:bg-gray-50"
              data-testid="ficha-adjust-hours"
            ><Wrench size={12} /> Ajustar horas</button>
          )}
          {packages.length === 0 ? (
            <div className="text-sm text-gray-400">Este cliente não possui Banco de Horas.</div>
          ) : (
            <div className="space-y-4">
              {packages.map((p) => {
                const left = Math.max(0, (p.hours_total || 0) - (p.hours_used || 0));
                const pct = p.hours_total ? Math.min(100, ((p.hours_used || 0) / p.hours_total) * 100) : 0;
                return (
                  <div key={p.id} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-sm font-bold text-[#14213D]">{hourBankName(p)}</div>
                      <Badge s={p.status === "active" ? "confirmed" : p.status} />
                    </div>
                    <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
                      <span>{p.hours_total}h adquiridas</span>
                      <span>{(p.hours_used || 0)}h utilizadas</span>
                      <span className="font-bold text-emerald-600">{left}h restantes</span>
                      {p.expires_at && <span>Validade: {fmtD(p.expires_at)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Próximas reservas */}
        <Card title="Próximas Reservas" icon={Calendar} testid="ficha-upcoming">
          {upcoming.length === 0 ? (
            <div className="text-sm text-gray-400">Nenhuma reserva futura.</div>
          ) : (
            <div className="space-y-2.5">
              {upcoming.slice(0, 6).map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 flex-wrap border-b border-gray-50 pb-2.5 last:border-0 last:pb-0">
                  <div className="text-sm">
                    <div className="font-semibold text-[#14213D] flex items-center gap-1.5"><MapPin size={12} className="text-gray-400" /> {r.space_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                      <Clock size={11} /> {fmtD(r.start_time)} · {fmtT(r.start_time)}–{fmtT(r.end_time)}
                      {r.checked_in_at && <span className="text-emerald-600 font-bold">· Check-in {fmtT(r.checked_in_at)}</span>}
                    </div>
                  </div>
                  <Badge s={r.status} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Histórico de reservas */}
      <Card title={`Histórico de Reservas (${reservations.length})`} icon={CheckCircle2} testid="ficha-reservations">
        {reservations.length === 0 ? (
          <div className="text-sm text-gray-400">Nenhuma reserva.</div>
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3">Data</th><th className="py-2 pr-3">Horário</th>
                  <th className="py-2 pr-3">Espaço</th><th className="py-2 pr-3">Duração</th>
                  <th className="py-2 pr-3">Contratação</th><th className="py-2 pr-3">Utilização</th>
                  <th className="py-2 pr-3">Valor</th><th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 last:border-0" data-testid={`ficha-res-${r.id}`}>
                    <td className="py-2.5 pr-3 whitespace-nowrap">{fmtD(r.start_time)}</td>
                    <td className="py-2.5 pr-3 whitespace-nowrap">{fmtT(r.start_time)}–{fmtT(r.end_time)}</td>
                    <td className="py-2.5 pr-3">{r.space_name}</td>
                    <td className="py-2.5 pr-3">{durH(r)}h</td>
                    <td className="py-2.5 pr-3 text-xs text-gray-500">
                      {r.origin === "package" || r.package_id ? "Banco de Horas" : r.source === "portal" ? "Portal (avulso)" : r.source || "Avulso"}
                    </td>
                    <td className="py-2.5 pr-3 text-xs" data-testid={`ficha-usage-${r.id}`}>
                      {r.checked_in_at ? (
                        <div className="space-y-0.5">
                          <div className="text-gray-600 whitespace-nowrap">
                            In {fmtT(r.checked_in_at)}{r.checked_out_at ? ` · Out ${fmtT(r.checked_out_at)}` : " · em utilização"}
                          </div>
                          {r.actual_hours_used != null && <div className="text-gray-500">{Number(r.actual_hours_used).toFixed(2).replace(".", ",")}h utilizadas</div>}
                          {Number(r.hours_consumed) > 0 && <div className="text-emerald-700 font-semibold">−{Number(r.hours_consumed).toFixed(2).replace(".", ",")}h do banco</div>}
                          {Number(r.overage_hours) > 0 && (
                            <div className="text-amber-600 font-semibold">
                              Excedente {Number(r.overage_hours).toFixed(2).replace(".", ",")}h · {formatBRL(r.overage_amount)}
                            </div>
                          )}
                        </div>
                      ) : "—"}
                    </td>
                    <td className="py-2.5 pr-3 whitespace-nowrap">{r.total_price ? formatBRL(r.total_price) : r.amount ? formatBRL(r.amount) : "—"}</td>
                    <td className="py-2.5"><Badge s={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pagamentos */}
        <Card title={`Pagamentos / Financeiro (${invoices.length})`} icon={Receipt} testid="ficha-invoices">
          {invoices.length === 0 ? (
            <div className="text-sm text-gray-400">Nenhum pagamento registrado.</div>
          ) : (
            <div className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1">
              {invoices.map((i) => (
                <div key={i.id} className="flex items-center justify-between gap-2 flex-wrap border-b border-gray-50 pb-2.5 last:border-0">
                  <div className="text-sm min-w-0">
                    <div className="font-semibold text-[#14213D] truncate">{i.description || "Cobrança"}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {fmtD(i.paid_at || i.created_at)} · {(i.method || "pix").toUpperCase() === "CARTAO" ? "Cartão" : (i.method || "pix").toUpperCase()}
                      <span className="ml-2 text-gray-400">#{(i.id || "").slice(0, 8)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-[#14213D]">{formatBRL(i.amount)}</div>
                    <Badge s={i.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Histórico operacional */}
        <Card title="Histórico" icon={Activity} testid="ficha-events">
          {(!events || events.length === 0) ? (
            <div className="text-sm text-gray-400">Nenhum evento registrado.</div>
          ) : (
            <div className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1">
              {events.map((e, idx) => (
                <div key={idx} className="flex items-start gap-2.5 text-xs border-b border-gray-50 pb-2.5 last:border-0">
                  <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-[#D4AF37] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold text-[#14213D]">{e.reason || e.origin || e.action}</div>
                    <div className="text-gray-400 mt-0.5">{fmtD(e.at)} {fmtT(e.at)}{e.by ? ` · ${e.by}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {editing && (
        <EditClientModal client={c} onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }} />
      )}
      {deleting && (
        <DeleteClientModal client={c} onClose={() => setDeleting(false)}
          onDeleted={() => nav("/admin/clientes")} />
      )}
      {adjusting && (
        <AdjustHoursModal clientId={c.id} onClose={() => setAdjusting(false)}
          onDone={() => { setAdjusting(false); load(); }} />
      )}
    </div>
  );
}
